<template>
  <div v-if="loading" class="skeleton-loader-wrapper">
    <SkeletonLoader :loadingText="loadingText" />
  </div>
  <div v-else class="edit-transaction-view">
    <MainHeader :title="'Add Transaction'">
      <template #left>
        <font-awesome-icon
          @click="router.push({ name: 'Transactions' })"
          :icon="faArrowLeftLong"
          class="pointer"
        />
      </template>
    </MainHeader>

    <div class="view add-transaction-view">
      <div class="at-header">
        <h1 class="at-title">New Transaction</h1>
        <p class="at-sub">Fill in the details below to add a transaction.</p>
      </div>

      <form @submit.prevent="createTransaction">
        <fieldset>
          <div class="ud-section">
            <span class="section-label">Details</span>

            <div class="ud-field">
              <label class="ud-label" for="transactionName">
                Name <span class="ud-asterisk">*</span>
              </label>
              <input
                id="transactionName"
                type="text"
                class="ud-input"
                v-model.trim="transactionName"
                placeholder="e.g. Whole Foods"
                required
              />
            </div>

            <div class="ud-field mt">
              <label class="ud-label" for="transactiondescription"
                >Description</label
              >
              <input
                type="text"
                class="ud-input"
                id="transactiondescription"
                v-model="transactionDescription"
                placeholder="Optional"
              />
            </div>

            <div class="ud-field mt">
              <label class="ud-label" for="transactionamount">
                Amount <span class="ud-asterisk">*</span>
              </label>
              <input
                id="transactionamount"
                type="text"
                class="ud-input"
                v-model="transactionAmount"
                placeholder="0.00"
                required
              />
            </div>

            <div class="ud-field mt">
              <label class="ud-label" for="transactiondate">Date</label>
              <input
                id="transactiondate"
                type="date"
                class="ud-input date-picker"
                v-model="transactionDate"
              />
            </div>
          </div>

          <div class="ud-section">
            <span class="section-label">Category</span>

            <div class="ud-field">
              <label class="ud-label" for="transactionCategory">Category</label>
              <select
                id="transactionCategory"
                v-model="transactionParentCategory"
                class="ud-input ud-select form-select"
              >
                <option value="" selected disabled>Select category</option>
                <option
                  v-for="parentCategory in store.categories"
                  :key="parentCategory"
                  :value="parentCategory"
                >
                  {{ parentCategory.name }}
                </option>
              </select>
            </div>

            <div
              class="ud-field mt"
              v-if="
                transactionParentCategory &&
                transactionParentCategory.subcategories != null
              "
            >
              <label class="ud-label" for="transactionSubcategory"
                >Subcategory</label
              >
              <select
                id="transactionSubcategory"
                v-model="transactionSubcategory"
                class="ud-input ud-select form-select"
              >
                <option value="" disabled selected>Select subcategory</option>
                <option
                  v-for="subcategory in transactionParentCategory.subcategories"
                  :key="subcategory"
                  :value="subcategory"
                >
                  {{ subcategory.name }}
                </option>
              </select>
            </div>
          </div>

          <div class="ud-section">
            <span class="section-label">Transaction type</span>
            <div class="toggle-row">
              <span class="ud-label">Money in (deposit / income)</span>
              <Toggle
                id="isIncome"
                :toggleActive="isIncome"
                @toggleSelected="toggleDeposit($event)"
              />
            </div>
          </div>

          <button
            role="button"
            type="submit"
            class="ud-submit"
            aria-label="Create Transaction"
          >
            Create Transaction
          </button>
        </fieldset>
      </form>
    </div>
  </div>
</template>

<script>
import { useMyStore } from '../store';
import { useRouter } from 'vue-router';
import { onMounted, ref } from 'vue';
import Toggle from '../components/Toggle.vue';
import MainHeader from '../components/View-Header.vue';
import {
  faHandHoldingDollar,
  faArrowLeftLong,
} from '@fortawesome/free-solid-svg-icons';
import SkeletonLoader from '../components/SkeletonLoader.vue';

export default {
  components: { MainHeader, SkeletonLoader, Toggle },
  setup() {
    const store = useMyStore();
    const router = useRouter();
    let transactionName = ref('');
    let transactionDescription = ref('');
    let transactionAmount = ref('');
    let transactionParentCategory = ref(null);
    let transactionSubcategory = ref(null);
    // let transactionName = ref();
    // let transactionDescription = ref();
    // let transactionAmount = ref();
    // let transactionParentCategory = ref();
    // const today = new Date();
    // const offsetDate = new Date(
    //   today.getTime() - today.getTimezoneOffset() * 60000
    // );
    // const formattedDate = offsetDate.toISOString().split('T')[0];
    // let transactionDate = ref(formattedDate);
    // let transactionSubcategory = ref();
    const loading = ref(true);
    let loadingText = ref('Loading...');
    const isIncome = ref(false);

    const getTodayLocalDate = () => {
      const today = new Date();
      const offsetDate = new Date(
        today.getTime() - today.getTimezoneOffset() * 60000
      );
      return offsetDate.toISOString().split('T')[0];
    };

    const resetForm = () => {
      transactionName.value = '';
      transactionDescription.value = '';
      transactionAmount.value = '';
      transactionParentCategory.value = null;
      transactionSubcategory.value = null;
      isIncome.value = false;
      transactionDate.value = getTodayLocalDate();
    };

    let transactionDate = ref(getTodayLocalDate());

    const toggleDeposit = (event) => {
      isIncome.value = event;
    };

    const createTransaction = async () => {
      try {
        loadingText.value = 'Creating Transaction...';
        loading.value = true;

        const newTransaction = {
          isEdited: false,
          TransactionCategoryID: transactionParentCategory.value
            ? transactionParentCategory.value.categoryID
            : null,
          TransactionSubcategoryID: transactionSubcategory.value
            ? transactionSubcategory.value.subcategoryID
            : null,
          displayName: transactionName.value,
          displayDescription: transactionDescription.value,
          transactionDate: transactionDate.value
            ? (() => {
                const [year, month, day] = transactionDate.value.split('-');
                return new Date(Date.UTC(year, month - 1, day)).toISOString();
              })()
            : null,
          amount: parseFloat(transactionAmount.value),
          category: transactionParentCategory.value || null,
          markedAsTransfer: false,
          hidden: false,
          transactionDeleted: false,
          isIncome: isIncome.value,
        };

        const response = await store.createTransaction(newTransaction);

        let createdId = null;
        const raw = response?.data;

        if (typeof raw === 'string') {
          try {
            const parsed = JSON.parse(raw);
            createdId =
              parsed?.userTransactionID ??
              parsed?.UserTransactionID ??
              parsed?.id ??
              null;
          } catch {
            createdId = raw;
          }
        } else if (typeof raw === 'number') {
          createdId = raw;
        } else if (raw && typeof raw === 'object') {
          createdId =
            raw.userTransactionID ?? raw.UserTransactionID ?? raw.id ?? null;
        }

        resetForm();

        if (createdId) {
          await router.push({
            name: 'EditTransaction',
            params: { userTransactionID: createdId },
          });
        } else {
          await router.push({ name: 'Transactions' });
        }
      } catch (error) {
        console.error('Error creating transaction:', error);
      } finally {
        loading.value = false;
      }
    };

    // const createTransaction = () => {
    //   loadingText.value = 'Creating Transaction...';
    //   loading.value = true;
    //   const newTransaction = {
    //     isEdited: false,
    //     TransactionCategoryID: transactionParentCategory.value
    //       ? transactionParentCategory.value.categoryID
    //       : null,
    //     TransactionSubcategoryID: transactionSubcategory.value
    //       ? transactionSubcategory.value.subcategoryID
    //       : null,
    //     displayName: transactionName.value,
    //     displayDescription: transactionDescription.value,
    //     transactionDate: transactionDate.value
    //       ? (() => {
    //           const [year, month, day] = transactionDate.value.split('-');
    //           return new Date(Date.UTC(year, month - 1, day)).toISOString();
    //         })()
    //       : null,
    //     // amount: transactionAmount.value,
    //     amount: parseFloat(transactionAmount.value),
    //     category: transactionParentCategory.value
    //       ? transactionParentCategory.value
    //       : null,
    //     markedAsTransfer: false,
    //     hidden: false,
    //     transactionDeleted: false,
    //     isIncome: isIncome.value,
    //   };
    //   store.createTransaction(newTransaction).then((response) => {
    //     loading.value = false;
    //     const data = typeof response.data === 'string'
    //       ? JSON.parse(response.data)
    //       : response.data;
    //     router.push({
    //       name: 'EditTransaction',
    //       params: { userTransactionID: data.userTransactionID },
    //     });
    //   });
    // };

    onMounted(async () => {
      if (!store.categories || store.categories.length === 0) {
        await store.getAllCategories();
      }
      loading.value = false;
    });

    return {
      store,
      router,
      faHandHoldingDollar,
      faArrowLeftLong,
      transactionName,
      transactionDescription,
      transactionAmount,
      transactionParentCategory,
      transactionDate,
      createTransaction,
      transactionSubcategory,
      loading,
      loadingText,
      isIncome,
      toggleDeposit,
    };
  },
};
</script>

<style scoped>
.add-transaction-view {
  max-width: 480px;
  margin: 0 auto;
}

.at-header {
  text-align: center;
  padding: 1.75rem 0 0.25rem;
  margin-bottom: 1.5rem;
}

.at-title {
  font-size: 1.3rem;
  font-weight: 700;
  color: #004751;
  margin: 0 0 0.3rem;
  letter-spacing: -0.02em;
}

.dark-theme .at-title {
  color: #5dcaa5;
}

.at-sub {
  font-size: 0.85rem;
  color: #888;
  margin: 0;
}
</style>

<!-- <template>
  <div v-if="loading" class="skeleton-loader-wrapper">
    <SkeletonLoader :loadingText="loadingText" />
  </div>
  <div v-else class="edit-transaction-view">
    <div>
      <MainHeader :title="'Add Transaction'">
        <template #left>
          <font-awesome-icon
            @click="router.push({ name: 'Transactions' })"
            :icon="faArrowLeftLong"
            class="pointer"
          />
        </template>
      </MainHeader>
    </div>
    <div class="view add-transaction">
      <div class="text-center pt-4 pb-4">
        <font-awesome-icon
          aria-label="transaction category icon"
          :icon="faHandHoldingDollar"
          class="icon view-icon"
        ></font-awesome-icon>
      </div>
      <form @submit.prevent="createTransaction">
        <fieldset>
          <div>
            <label for="transactionName" class="small-grey-text">Name *</label>
            <input
              id="transactionName"
              type="text"
              class="input"
              v-model.trim="transactionName"
              required
            />
          </div>
          <div class="mt-1">
            <label for="transactiondescription" class="small-grey-text"
              >Description</label
            >
            <input
              type="text"
              class="input"
              id="transactiondescription"
              v-model="transactionDescription"
            />
          </div>
          <div class="mt-1">
            <label for="transactionamount" class="small-grey-text"
              >Amount *</label
            >
            <input
              id="transactionamount"
              type="text"
              class="input"
              v-model="transactionAmount"
              required
            />
          </div>
          <div class="mt-1">
            <label for="transactionCategory" class="small-grey-text"
              >Category</label
            >
            <select
              id="transactionCategory"
              v-model="transactionParentCategory"
              class="form-select grey-outline-form-select"
            >
              <option value="" selected disabled>Select Category</option>
              <option
                v-for="parentCategory in store.categories"
                :key="parentCategory"
                :value="parentCategory"
              >
                {{ parentCategory.name }}
              </option>
            </select>
          </div>
          <div
            class="mt-1"
            v-if="
              transactionParentCategory &&
              transactionParentCategory.subcategories != null
            "
          >
            <label for="transactionSubcategory" class="small-grey-text"
              >Subcategory</label
            >
            <select
              id="transactionSubcategory"
              v-model="transactionSubcategory"
              class="form-select grey-form-select"
            >
              <option value="" disabled selected>Select Subcategory</option>
              <option
                v-for="subcategory in transactionParentCategory.subcategories"
                :key="subcategory"
                :value="subcategory"
              >
                {{ subcategory.name }}
              </option>
            </select>
          </div>
          <div class="mt-1">
            <label for="transactiondate" class="small-grey-text block"
              >Date</label
            >
            <input
              id="transactiondate"
              type="date"
              class="input date-picker"
              v-model="transactionDate"
            />
          </div>
          <div class="mt-1">
            <label for="transactiondate" class="small-grey-text block"
              >Deposit (Money In)</label
            >
            <Toggle
              id="isIncome"
              :toggleActive="isIncome"
              @toggleSelected="toggleDeposit($event)"
            />
          </div>
          <div class="mt-8">
            <button
              role="button"
              type="submit"
              class="teal-button w-100"
              aria-label="Create Transaction"
            >
              Create Transaction
            </button>
          </div>
        </fieldset>
      </form>
    </div>
  </div>
</template>

<script>
import { useMyStore } from '../store';
import { useRouter } from 'vue-router';
import { onMounted, ref } from 'vue';
import Toggle from '../components/Toggle.vue';
import MainHeader from '../components/View-Header.vue';
import {
  faHandHoldingDollar,
  faArrowLeftLong,
} from '@fortawesome/free-solid-svg-icons';
import SkeletonLoader from '../components/SkeletonLoader.vue';

export default {
  components: { MainHeader, SkeletonLoader, Toggle },
  setup() {
    const store = useMyStore();
    const router = useRouter();
    let transactionName = ref();
    let transactionDescription = ref();
    let transactionAmount = ref();
    let transactionParentCategory = ref();
    const today = new Date();
    const offsetDate = new Date(
      today.getTime() - today.getTimezoneOffset() * 60000
    );
    const formattedDate = offsetDate.toISOString().split('T')[0];
    let transactionDate = ref(formattedDate);
    let transactionSubcategory = ref();
    const loading = ref(true);
    let loadingText = ref('Loading...');
    const isIncome = ref(false);

    const toggleDeposit = (event) => {
      isIncome.value = event;
    };

    const createTransaction = () => {
      loadingText.value = 'Creating Transaction...';
      loading.value = true;
      const newTransaction = {
        isEdited: false,
        TransactionCategoryID: transactionParentCategory.value
          ? transactionParentCategory.value.categoryID
          : null,
        TransactionSubcategoryID: transactionSubcategory.value
          ? transactionSubcategory.value.subcategoryID
          : null,
        displayName: transactionName.value,
        displayDescription: transactionDescription.value,
        transactionDate: transactionDate.value
          ? (() => {
              const [year, month, day] = transactionDate.value.split('-');
              return new Date(Date.UTC(year, month - 1, day)).toISOString();
            })()
          : null,
        amount: transactionAmount.value,
        category: transactionParentCategory.value
          ? transactionParentCategory.value
          : null,
        markedAsTransfer: false,
        hidden: false,
        transactionDeleted: false,
        isIncome: isIncome.value,
      };
      store.createTransaction(newTransaction).then((response) => {
        loading.value = false;
        router.push({
          name: 'EditTransaction',
          params: { userTransactionID: response.data },
        });
      });
    };

    onMounted(() => {
      loading.value = false;
    });

    return {
      store,
      router,
      faHandHoldingDollar: faHandHoldingDollar,
      faArrowLeftLong: faArrowLeftLong,
      transactionName,
      transactionDescription,
      transactionAmount,
      transactionParentCategory,
      transactionDate,
      createTransaction,
      transactionSubcategory,
      loading,
      loadingText,
      isIncome,
      toggleDeposit,
    };
  },
};
</script>

<style scoped>
.add-transaction .icon {
  color: #e8f44a;
  text-align: center;
}

.add-transaction .input {
  width: 100%;
  box-sizing: border-box;
  font-size: 1em;
}
</style> -->
